import React, { memo, useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ReportData } from "../../types/global.d";
import { getReportColor, getReportTitle } from "../../utils/reportProps";
import { useImageUrls } from "../hooks/useReportPanel";
import { useUserContext } from "../../contexts/UserContext";
import { useDataContext } from "../../contexts/DataContext";
import { UserStatus } from "../../constants";
import { getAuthToken } from "../../api/auth";
import {
  verifyReport as apiVerifyReport,
  verifyCleaned as apiVerifyCleaned,
  deleteReport as apiDeleteReport,
  submitNotCleaned as apiSubmitNotCleaned,
} from "../../api/reports";
import { getErrorMessage } from "../../utils/api";
import ReportPanelHeader from "./ReportPanelHeader";
import UnifiedImageCarousel from "./UnifiedImageCarousel";
import ReportDetailsContent from "./ReportDetailsContent";
import ReportDetailsSkeleton from "./ReportDetailsSkeleton";
import UserViewPanel from "../user/UserViewPanel";
import CleanupPage from "./CleanupPage";
import PhotoSelectionDialog from "../PhotoSelectionDialog";
import EditReportDialog from "./EditReportDialog";
import EditCleanupDialog from "./EditCleanupDialog";
import ComplaintDraftDialog from "./ComplaintDraftDialog";
import { buildReportShareUrl } from "../hooks/useReportUrlSync";
import "../styles/ReportDetailsPanel.css";

interface ReportDetailsPanelProps {
  report: ReportData | null;
  // All reports in the opened cluster (>=2) so the panel can offer a selector
  // to page between them. Empty when a single pin is selected.
  previewReports?: ReportData[];
  onSelectPreviewReport?: (reportId: string) => void;
  onClose: () => void;
  onUserFilterChange?: (nickname: string | null) => void;
  onOpenChatWithUser?: (nickname: string) => void;
}

const ReportDetailsPanel: React.FC<ReportDetailsPanelProps> = memo(
  ({
    report,
    previewReports = [],
    onSelectPreviewReport,
    onClose,
    onUserFilterChange,
    onOpenChatWithUser,
  }) => {
    const { t } = useTranslation();
    const { reportImages, cleanupImages } = useImageUrls(report);
    const { user } = useUserContext();
    const { updateData } = useDataContext();
    const [viewMode, setViewMode] = useState<"report" | "cleaned">("report");
    const hasCleanedData = Boolean(report?.cleaned);
    const [viewingUser, setViewingUser] = useState<{
      nickname: string;
      avatar?: string;
      fullname?: string;
      vkUserId?: string;
    } | null>(null);
    const [showPhotoDialog, setShowPhotoDialog] = useState(false);
    const [showCleanupPage, setShowCleanupPage] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [useAdminImage, setUseAdminImage] = useState(false);
    const [adminImageVariant, setAdminImageVariant] = useState<
      "not_found" | undefined
    >(undefined);
    const [showEditReportDialog, setShowEditReportDialog] = useState(false);
    const [showEditCleanupDialog, setShowEditCleanupDialog] = useState(false);
    const [showComplaintDialog, setShowComplaintDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const isAdmin = user?.status === UserStatus.admin;
    const isOwner = user?.nickname === report?.user?.nickname;
    const canCreateComplaint = Boolean(
      isAdmin && report?.verified && !report?.cleaned
    );

    const statusColor = useMemo(
      () => (report ? getReportColor(report) : ""),
      [report]
    );
    const statusTitle = useMemo(
      () => (report ? getReportTitle(report) : ""),
      [report]
    );

    useEffect(() => {
      if (!report) {
        setViewMode("report");
        return;
      }

      setViewMode(report.cleaned ? "cleaned" : "report");
    }, [report]);

    const handleViewModeChange = useCallback(
      (mode: "report" | "cleaned") => {
        if (mode === "cleaned" && !report?.cleaned) {
          return;
        }
        setViewMode(mode);
      },
      [report?.cleaned]
    );

    const handleUserClick = useCallback(
      (
        nickname: string,
        avatar?: string,
        fullname?: string,
        vkUserId?: string
      ) => {
        setViewingUser({ nickname, avatar, fullname, vkUserId });
      },
      []
    );

    const handleUserViewClose = useCallback(() => {
      setViewingUser(null);
    }, []);

    const handleShare = useCallback(async () => {
      if (!report) return;
      const url = buildReportShareUrl(report._id);

      if (navigator.share) {
        try {
          await navigator.share({ title: t("report.shareText"), url });
        } catch (error: any) {
          // User dismissed the share sheet - not an error
          if (error?.name !== "AbortError") {
            console.error("Share failed:", error);
          }
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(url);
        alert(t("report.linkCopied"));
      } catch {
        // Clipboard unavailable (e.g. non-secure context) - let the user copy manually
        window.prompt(t("report.share"), url);
      }
    }, [report, t]);

    const handleMarkCleaned = useCallback(() => {
      setUseAdminImage(false);
      setAdminImageVariant(undefined);
      setShowPhotoDialog(true);
    }, []);

    const handlePhotoDialogClose = useCallback(() => {
      setShowPhotoDialog(false);
      setUseAdminImage(false);
      setAdminImageVariant(undefined);
    }, []);

    const handlePhotosSelected = useCallback((files: File[]) => {
      setSelectedFiles(files);
      setShowPhotoDialog(false);
      setShowCleanupPage(true);
    }, []);

    const handleAdminClean = useCallback(() => {
      setUseAdminImage(true);
      setAdminImageVariant(undefined);
      setShowPhotoDialog(false);
      setShowCleanupPage(true);
    }, []);

    const handleAdminNotFound = useCallback(() => {
      setUseAdminImage(true);
      setAdminImageVariant("not_found");
      setShowPhotoDialog(false);
      setShowCleanupPage(true);
    }, []);

    const handleCleanupPageClose = useCallback(() => {
      setShowCleanupPage(false);
      setSelectedFiles([]);
      setUseAdminImage(false);
      setAdminImageVariant(undefined);
    }, []);

    const handleCleanupSuccess = useCallback(() => {
      setShowCleanupPage(false);
      setSelectedFiles([]);
      setUseAdminImage(false);
      setAdminImageVariant(undefined);
      onClose();
    }, [onClose]);

    // Admin action handlers
    const handleVerifyReport = useCallback(async () => {
      if (!report || loading) return;
      const token = getAuthToken();
      if (!token) return;

      setLoading(true);
      try {
        await apiVerifyReport(report._id, token);
        await updateData();
        onClose();
        alert("Report verified successfully!");
      } catch (error) {
        alert(`Failed to verify report: ${getErrorMessage(error)}`);
      } finally {
        setLoading(false);
      }
    }, [report, loading, updateData, onClose]);

    const handleDeclineReport = useCallback(async () => {
      if (!report || loading) return;
      if (!window.confirm("Are you sure you want to decline this report?"))
        return;

      const token = getAuthToken();
      if (!token) return;

      setLoading(true);
      try {
        await apiDeleteReport(report._id, token);
        await updateData();
        onClose();
        alert("Report declined successfully!");
      } catch (error) {
        alert(`Failed to decline report: ${getErrorMessage(error)}`);
      } finally {
        setLoading(false);
      }
    }, [report, loading, updateData, onClose]);

    const handleEditReport = useCallback(() => {
      setShowEditReportDialog(true);
    }, []);

    const handleEditReportClose = useCallback(() => {
      setShowEditReportDialog(false);
    }, []);

    const handleEditReportSuccess = useCallback(() => {
      setShowEditReportDialog(false);
      onClose();
    }, [onClose]);

    const handleComplaintOpen = useCallback(() => {
      setShowComplaintDialog(true);
    }, []);

    const handleComplaintClose = useCallback(() => {
      setShowComplaintDialog(false);
    }, []);

    const handleVerifyCleaned = useCallback(async () => {
      if (!report || loading) return;
      const token = getAuthToken();
      if (!token) return;

      setLoading(true);
      try {
        await apiVerifyCleaned(report._id, token);
        await updateData();
        onClose();
        alert("Cleanup verified successfully!");
      } catch (error) {
        alert(`Failed to verify cleanup: ${getErrorMessage(error)}`);
      } finally {
        setLoading(false);
      }
    }, [report, loading, updateData, onClose]);

    const handleMarkNotCleaned = useCallback(async () => {
      if (!report || loading) return;
      if (
        !window.confirm(
          "Are you sure this location is not cleaned? This will remove the cleanup data."
        )
      )
        return;

      const token = getAuthToken();
      if (!token) return;

      setLoading(true);
      try {
        await apiSubmitNotCleaned(report._id, token);
        await updateData();
        onClose();
        alert("Cleanup marked as not cleaned successfully!");
      } catch (error) {
        alert(`Failed to mark as not cleaned: ${getErrorMessage(error)}`);
      } finally {
        setLoading(false);
      }
    }, [report, loading, updateData, onClose]);

    const handleEditCleaned = useCallback(() => {
      setShowEditCleanupDialog(true);
    }, []);

    const handleEditCleanupClose = useCallback(() => {
      setShowEditCleanupDialog(false);
    }, []);

    const handleEditCleanupSuccess = useCallback(() => {
      setShowEditCleanupDialog(false);
      onClose();
    }, [onClose]);

    if (!report) return null;

    // Full data not in yet: show placeholders + spinner and no action buttons
    // (which would otherwise render against unknown verified/cleaned fields).
    const isLoading = report.dataLevel !== "full";

    return (
      <div className="report-details-panel">
        <ReportPanelHeader
          statusColor={statusColor}
          statusTitle={statusTitle}
          onClose={onClose}
          onShare={handleShare}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          hasCleanedData={hasCleanedData}
          reportImagesCount={reportImages.length}
          cleanedImagesCount={cleanupImages.length}
        />

        {previewReports.length > 1 && (
          <div className="report-preview-selector">
            <div className="report-preview-selector__caption">
              {t("report.reportsCount", { count: previewReports.length })}
            </div>
            <div className="report-preview-selector__list">
              {previewReports.map((item) => {
                const isActive = item._id === report._id;
                const itemDate = item.cleaned?.date || item.date;
                return (
                  <button
                    key={item._id}
                    type="button"
                    className={`report-preview-chip${
                      isActive ? " active" : ""
                    }`}
                    onClick={() => onSelectPreviewReport?.(item._id)}
                    aria-pressed={isActive}
                  >
                    <span
                      className="report-preview-chip__dot"
                      style={{ backgroundColor: getReportColor(item) }}
                    />
                    <span className="report-preview-chip__text">
                      <span className="report-preview-chip__title">
                        {getReportTitle(item)}
                      </span>
                      {itemDate && (
                        <span className="report-preview-chip__date">
                          {itemDate}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="panel-content">
          <div className="image-section">
            {/* While the full report loads, show a shimmering placeholder. */}
            {isLoading && (
              <div className="image-skeleton skeleton-bar" aria-hidden="true" />
            )}

            {/* Show no images message if neither report nor cleaned images exist */}
            {!isLoading && reportImages.length === 0 && cleanupImages.length === 0 && (
              <div className="no-image-placeholder">
                <svg
                  width="80"
                  height="60"
                  viewBox="0 0 80 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="5"
                    y="5"
                    width="70"
                    height="50"
                    rx="4"
                    stroke="#E7E4D8"
                    strokeWidth="2"
                    fill="#F7F6EF"
                  />
                  <path d="M20 20L30 35L45 20L60 45H20V20Z" fill="#E7E4D8" />
                  <circle cx="30" cy="25" r="4" fill="#E7E4D8" />
                </svg>
                <p>No images available</p>
              </div>
            )}

            {/* Unified carousel - only show if there are any images */}
            {!isLoading &&
              (reportImages.length > 0 || cleanupImages.length > 0) && (
                <UnifiedImageCarousel
                  reportImages={reportImages}
                  cleanedImages={cleanupImages}
                  showPreviews={true}
                  activeImageType={viewMode}
                  onImageTypeChange={handleViewModeChange}
                />
              )}
          </div>

          {isLoading ? (
            <ReportDetailsSkeleton />
          ) : (
            <ReportDetailsContent
              report={report}
              onUserClick={handleUserClick}
              onMarkCleaned={handleMarkCleaned}
              isAdmin={isAdmin}
              isOwner={isOwner}
              onVerifyReport={handleVerifyReport}
              onDeclineReport={handleDeclineReport}
              onEditReport={handleEditReport}
              onVerifyCleaned={handleVerifyCleaned}
              onMarkNotCleaned={handleMarkNotCleaned}
              onEditCleaned={handleEditCleaned}
              onCreateComplaint={
                canCreateComplaint ? handleComplaintOpen : undefined
              }
              viewMode={viewMode}
            />
          )}
        </div>

        {/* User View Panel */}
        {viewingUser && (
          <UserViewPanel
            nickname={viewingUser.nickname}
            avatar={viewingUser.avatar}
            fullname={viewingUser.fullname}
            vkUserId={viewingUser.vkUserId}
            onClose={handleUserViewClose}
            onUserFilterChange={onUserFilterChange}
            onOpenChatWithUser={onOpenChatWithUser}
          />
        )}

        {/* Photo Selection Dialog for Mark as Cleaned */}
        {showPhotoDialog && (
          <PhotoSelectionDialog
            onClose={handlePhotoDialogClose}
            onPhotosSelected={handlePhotosSelected}
            onAdminClean={handleAdminClean}
            onAdminNotFound={handleAdminNotFound}
            isAdmin={isAdmin}
          />
        )}

        {/* Cleanup Page */}
        {showCleanupPage &&
          report &&
          (useAdminImage || selectedFiles.length > 0) && (
            <CleanupPage
              reportId={report._id}
              files={selectedFiles}
              useAdminImage={useAdminImage}
              adminImageVariant={adminImageVariant}
              onClose={handleCleanupPageClose}
              onSuccess={handleCleanupSuccess}
            />
          )}

        {/* Edit Report Dialog */}
        {showEditReportDialog && report && (
          <EditReportDialog
            report={report}
            onClose={handleEditReportClose}
            onSuccess={handleEditReportSuccess}
          />
        )}

        {/* Edit Cleanup Dialog */}
        {showEditCleanupDialog && report?.cleaned && (
          <EditCleanupDialog
            cleanedData={report.cleaned}
            reportId={report._id}
            onClose={handleEditCleanupClose}
            onSuccess={handleEditCleanupSuccess}
          />
        )}

        {/* Complaint Draft Dialog */}
        {showComplaintDialog && report && (
          <ComplaintDraftDialog
            report={report}
            user={user}
            onClose={handleComplaintClose}
          />
        )}
      </div>
    );
  }
);

ReportDetailsPanel.displayName = "ReportDetailsPanel";

export default ReportDetailsPanel;
